// Domain-warped value noise, rendered small and scaled up smooth: the
// flowing colour of the old Neon background and the smoke under Flames.
// Deterministic in (x, y, t), so preview and export match frame for frame.

const PERM = (() => {
  const p = Array.from({ length: 256 }, (_, i) => i);
  let s = 1337;
  for (let i = 255; i > 0; i--) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const j = s % (i + 1);
    [p[i], p[j]] = [p[j], p[i]];
  }
  return new Uint8Array([...p, ...p]);
})();

const fade = (t: number) => t * t * (3 - 2 * t);

function value(x: number, y: number): number {
  const xi = Math.floor(x),
    yi = Math.floor(y);
  const xf = x - xi,
    yf = y - yi;
  const X = xi & 255,
    Y = yi & 255;
  const a = PERM[PERM[X] + Y],
    b = PERM[PERM[X + 1] + Y],
    c = PERM[PERM[X] + Y + 1],
    d = PERM[PERM[X + 1] + Y + 1];
  const u = fade(xf),
    v = fade(yf);
  return ((a + (b - a) * u) * (1 - v) + (c + (d - c) * u) * v) / 255;
}

function fbm(x: number, y: number): number {
  return (
    value(x, y) * 0.57 +
    value(x * 2.03 + 17, y * 2.03 + 9) * 0.29 +
    value(x * 4.1 + 41, y * 4.1 + 23) * 0.14
  );
}

/** 0..1, flowing over time. `warp` is how far the field folds into itself. */
export function warped(x: number, y: number, t: number, warp = 2.6): number {
  const qx = fbm(x + t * 0.11, y - t * 0.07);
  const qy = fbm(x + 5.2 - t * 0.06, y + 1.3 + t * 0.09);
  return fbm(x + warp * qx + t * 0.03, y + warp * qy);
}

type Rgb = [number, number, number];
export const hex = (h: string): Rgb => [
  parseInt(h.slice(1, 3), 16),
  parseInt(h.slice(3, 5), 16),
  parseInt(h.slice(5, 7), 16),
];

function paletteAt(stops: Rgb[], v: number): Rgb {
  const x = Math.max(0, Math.min(0.9999, v)) * (stops.length - 1);
  const i = Math.floor(x),
    f = fade(x - i);
  const a = stops[i],
    b = stops[i + 1];
  return [
    a[0] + (b[0] - a[0]) * f,
    a[1] + (b[1] - a[1]) * f,
    a[2] + (b[2] - a[2]) * f,
  ];
}

type Small = {
  canvas: OffscreenCanvas | HTMLCanvasElement;
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  img: ImageData;
};
const smalls = new Map<string, Small>();
function small(w: number, h: number): Small {
  const key = `${w}x${h}`;
  let s = smalls.get(key);
  if (!s) {
    const canvas =
      typeof OffscreenCanvas !== "undefined"
        ? new OffscreenCanvas(w, h)
        : Object.assign(document.createElement("canvas"), {
            width: w,
            height: h,
          });
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
    s = { canvas, ctx, img: ctx.createImageData(w, h) };
    smalls.set(key, s);
  }
  return s;
}

/**
 * Paints the noise field across W×H through `stops`. `alpha(v, y01)` sets
 * each cell's opacity (1 for an opaque field). `scale` is noise cells
 * across the short side.
 */
export function paintNoise(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  W: number,
  H: number,
  t: number,
  stops: Rgb[],
  scale: number,
  alpha: (v: number, y01: number) => number = () => 1,
  // Fractal noise sits near 0.5; stretching it reaches the palette's ends.
  contrast = 1,
  // Neon's field is soft: less folding, and blurred before it's scaled up.
  warp = 2.6,
  soften = 0,
) {
  const gw = W >= H ? 160 : 90;
  const gh = Math.max(1, Math.round((gw * H) / W));
  const s = small(gw, gh);
  const data = s.img.data;
  const k = scale / Math.min(gw, gh);
  for (let y = 0; y < gh; y++) {
    for (let x = 0; x < gw; x++) {
      const v = (warped(x * k, y * k, t, warp) - 0.5) * contrast + 0.5;
      const [r, g, b] = paletteAt(stops, v);
      const o = (y * gw + x) * 4;
      data[o] = r;
      data[o + 1] = g;
      data[o + 2] = b;
      data[o + 3] = Math.round(255 * alpha(v, y / gh));
    }
  }
  s.ctx.putImageData(s.img, 0, 0);
  let source: OffscreenCanvas | HTMLCanvasElement = s.canvas;
  if (soften > 0) {
    // Blur at the small size (cheap), padded so the edges don't go thin.
    const b = small(gw + 1, gh + 1);
    b.ctx.save();
    b.ctx.filter = `blur(${soften}px)`;
    b.ctx.drawImage(s.canvas, -2, -2, gw + 5, gh + 5);
    b.ctx.restore();
    source = b.canvas;
  }
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, W, H);
  ctx.restore();
}
